// @flow

import React from 'react';
import * as Yup from 'yup';
import * as Actions from 'main-app/store/Actions';
import { i18n } from 'shared/utils';
import AddGLAccountMutation from 'main-app/mutations/AddGLAccount';
import UpdateGLAccountMutation from 'main-app/mutations/UpdateGLAccount';
import Button from 'shared/components/common/Button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
} from 'shared/components/form';

type Props = {
  onClose: () => void,
  account?: ?Object,
};

const ACCOUNT_TYPE_OPTIONS = [
  { label: 'Asset', value: 'ASSET' },
  { label: 'Liability', value: 'LIABILITY' },
  { label: 'Equity', value: 'EQUITY' },
  { label: 'Revenue', value: 'REVENUE' },
  { label: 'COGS', value: 'COGS' },
  { label: 'Expense', value: 'EXPENSE' },
];

const NORMAL_BALANCE_OPTIONS = [
  { label: 'Debit', value: 'DEBIT' },
  { label: 'Credit', value: 'CREDIT' },
];

const AddUpdateGLAccountModal = ({ onClose, account }: Props) => {
  const isEditing = !!account;

  const language = {
    title: isEditing ? i18n.t('Edit Account') : i18n.t('New Account'),
    submitButton: isEditing ? i18n.t('Save') : i18n.t('Create Account'),
  };

  return (
    <Modal isOpen onClose={onClose}>
      <Formik
        initialValues={{
          accountNumber: account ? account.accountNumber : '',
          name: account ? account.name : '',
          description: account ? account.description || '' : '',
          accountType: account ? account.accountType : 'ASSET',
          normalBalance: account ? account.normalBalance : 'DEBIT',
          isActive: account ? account.isActive : true,
        }}
        validationSchema={Yup.object().shape({
          accountNumber: Yup.string().required(
            i18n.t('Account number is required'),
          ),
          name: Yup.string().required(i18n.t('Account name is required')),
          accountType: Yup.string().required(
            i18n.t('Account type is required'),
          ),
          normalBalance: Yup.string().required(
            i18n.t('Normal balance is required'),
          ),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          const input = {
            accountNumber: values.accountNumber.trim(),
            name: values.name.trim(),
            description: values.description.trim() || null,
            accountType: values.accountType,
            normalBalance: values.normalBalance,
            isActive: values.isActive,
          };

          const MutationToUse = isEditing
            ? UpdateGLAccountMutation
            : AddGLAccountMutation;
          const mutationInput = isEditing
            ? { ...input, id: account.id }
            : input;

          try {
            await MutationToUse.commit({
              variables: { input: mutationInput },
            });
            Actions.alertNotification(
              isEditing
                ? i18n.t('Account updated successfully.')
                : i18n.t('Account created successfully.'),
              'success',
            );
            onClose();
          } catch (e) {
            Actions.alertNotification(
              isEditing
                ? i18n.t('Error updating account.')
                : i18n.t('Error creating account.'),
              'error',
            );
            setSubmitting(false);
          }
        }}
        render={({
          errors,
          values,
          isValid,
          isSubmitting,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit}>
            <ModalHeader header={language.title} onClose={onClose} />
            <ModalBody withPadding>
              <FieldGroupRow>
                <FieldGroup
                  name="accountNumber"
                  label={i18n.t('Account Number')}
                  error={errors.accountNumber}
                />
                <FieldGroup
                  name="name"
                  label={i18n.t('Account Name')}
                  error={errors.name}
                />
              </FieldGroupRow>
              <FieldGroup
                name="description"
                label={i18n.t('Description')}
                type="textarea"
              />
              <FieldGroupRow>
                <FieldGroup
                  name="accountType"
                  label={i18n.t('Account Type')}
                  type="select"
                  options={ACCOUNT_TYPE_OPTIONS}
                  error={errors.accountType}
                />
                <FieldGroup
                  name="normalBalance"
                  label={i18n.t('Normal Balance')}
                  type="select"
                  options={NORMAL_BALANCE_OPTIONS}
                  error={errors.normalBalance}
                />
              </FieldGroupRow>
              {isEditing && (
                <FieldGroup
                  name="isActive"
                  label={i18n.t('Active')}
                  type="toggle"
                />
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={onClose}>
                {i18n.t('Cancel')}
              </Button>
              <Button
                type="submit"
                theme="blue"
                disabled={!isValid}
                loading={isSubmitting}
              >
                {language.submitButton}
              </Button>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

export default AddUpdateGLAccountModal;
